import { Check, Copy, ExternalLink, RefreshCw, Terminal } from "lucide-react";
import { useState } from "react";
import Modal from "@/components/ui/Modal";

interface RocketPayResponseModalProps {
  open: boolean;
  onClose: () => void;
  mandateId: string;
  responseObject: any;
  loading?: boolean;
  onRefresh?: () => void;
  onTriggerPostRefresh?: () => void;
  refreshingPost?: boolean;
  onTriggerCancel?: () => void;
  cancelling?: boolean;
  onTriggerDelete?: () => void;
  deleting?: boolean;
}

export default function RocketPayResponseModal({
  open,
  onClose,
  mandateId,
  responseObject,
  loading = false,
  onRefresh,
  onTriggerPostRefresh,
  refreshingPost = false,
  onTriggerCancel,
  cancelling = false,
  onTriggerDelete,
  deleting = false,
}: RocketPayResponseModalProps) {
  const [copied, setCopied] = useState(false);

  const jsonString = responseObject
    ? JSON.stringify(responseObject, null, 2)
    : "// No response data available";

  function copyJson() {
    navigator.clipboard.writeText(jsonString).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const state = (responseObject?.state || responseObject?.status || "UNKNOWN").toUpperCase();
  const isSuccess = ["ACTIVATED", "ACTIVE", "APPROVED"].includes(state);
  const isCancelled = ["CANCELLED", "REVOKED"].includes(state);

  return (
    <Modal open={open} onClose={onClose} title="Gateway Mandate Entity Details" size="lg">
      <div className="flex flex-col gap-4">
        {/* API Endpoint & Actions Info */}
        <div className="rounded-lg bg-slate-900 p-3.5 text-xs text-slate-200">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sky-400 font-mono font-semibold">
              <Terminal className="h-3.5 w-3.5" />
              <span>/v4/mandates/{mandateId}</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {onTriggerPostRefresh && (
                <button
                  type="button"
                  onClick={onTriggerPostRefresh}
                  disabled={refreshingPost || loading}
                  className="flex items-center gap-1.5 rounded bg-sky-500/20 hover:bg-sky-500/30 text-sky-300 px-2.5 py-1 text-[11px] font-mono border border-sky-500/40 transition disabled:opacity-50 cursor-pointer"
                  title="Trigger POST /v4/mandates/:id/refresh"
                >
                  <RefreshCw className={`h-3 w-3 ${refreshingPost ? "animate-spin" : ""}`} />
                  <span>POST /refresh</span>
                </button>
              )}
              {onTriggerCancel && (
                <button
                  type="button"
                  onClick={onTriggerCancel}
                  disabled={cancelling || loading || isCancelled}
                  className="flex items-center gap-1.5 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 px-2.5 py-1 text-[11px] font-mono border border-amber-500/40 transition disabled:opacity-50 cursor-pointer"
                  title="Trigger POST /v4/mandates/:id/cancel (Requires ACTIVATED state)"
                >
                  <RefreshCw className={`h-3 w-3 ${cancelling ? "animate-spin" : ""}`} />
                  <span>POST /cancel</span>
                </button>
              )}
              {onTriggerDelete && (
                <button
                  type="button"
                  onClick={onTriggerDelete}
                  disabled={deleting || loading || isCancelled}
                  className="flex items-center gap-1.5 rounded bg-red-500/20 hover:bg-red-500/30 text-red-300 px-2.5 py-1 text-[11px] font-mono border border-red-500/40 transition disabled:opacity-50 cursor-pointer"
                  title="Trigger DELETE /v4/mandates/:id (Requires CREATED state)"
                >
                  <RefreshCw className={`h-3 w-3 ${deleting ? "animate-spin" : ""}`} />
                  <span>DELETE</span>
                </button>
              )}
            </div>
          </div>
          <div className="mt-2.5 grid grid-cols-1 md:grid-cols-2 gap-1 text-[11px] text-slate-400 font-mono">
            <div>
              <span className="text-slate-500">x-app-context:</span> MERCHANT_API
            </div>
            <div>
              <span className="text-slate-500">x-token:</span> &lt;token&gt;
            </div>
          </div>
        </div>

        {/* Quick Details Badges */}
        {responseObject && (
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                isSuccess
                  ? "bg-emerald-100 text-emerald-800"
                  : state === "FAILED"
                  ? "bg-red-100 text-red-800"
                  : "bg-amber-100 text-amber-800"
              }`}
            >
              State: {state}
            </span>
            {responseObject?.mode && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
                Mode: {responseObject.mode}
              </span>
            )}
            {responseObject?.reference_id && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
                Ref: {responseObject.reference_id}
              </span>
            )}
            {(responseObject?.umrn || responseObject?.meta?.umrn) && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
                UMRN: {responseObject?.umrn || responseObject?.meta?.umrn}
              </span>
            )}
            {responseObject?.meta?.mandate_auth_checkout_url && (
              <a
                href={responseObject.meta.mandate_auth_checkout_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 underline font-medium ml-auto"
              >
                Checkout Link <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        )}

        {/* JSON Code Viewer */}
        <div className="relative rounded-lg border border-slate-200 bg-slate-950 overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-800 bg-slate-900/90 px-3 py-2 text-xs text-slate-400">
            <span>Response Payload (JSON)</span>
            <div className="flex items-center gap-2">
              {onRefresh && (
                <button
                  type="button"
                  onClick={onRefresh}
                  disabled={loading}
                  className="flex items-center gap-1 rounded px-2 py-1 text-xs text-slate-300 hover:bg-slate-800 hover:text-white transition disabled:opacity-50 cursor-pointer"
                  title="Fetch fresh data from Gateway"
                >
                  <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
                  Refresh
                </button>
              )}
              <button
                type="button"
                onClick={copyJson}
                className="flex items-center gap-1 rounded px-2 py-1 text-xs text-slate-300 hover:bg-slate-800 hover:text-white transition cursor-pointer"
              >
                {copied ? (
                  <>
                    <Check className="h-3 w-3 text-emerald-400" />
                    <span className="text-emerald-400">Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-3 w-3" />
                    Copy JSON
                  </>
                )}
              </button>
            </div>
          </div>

          <pre className="max-h-[380px] overflow-auto p-4 text-xs font-mono text-emerald-400 leading-relaxed scrollbar-thin whitespace-pre-wrap break-all">
            {loading ? "// Fetching latest mandate object from Gateway..." : jsonString}
          </pre>
        </div>
      </div>
    </Modal>
  );
}
